import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Calendar, ArrowRight, Tag } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import useJsonLd from "../../hooks/useJsonLd";
import { api } from "../../lib/api";

const fmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export function ArticlesList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/articles").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  usePageMeta("Travel Guides — Farevault by Sellvago", "Long-form travel guides for Pakistan travellers: Umrah, Gulf routes, budget tips and airline comparisons.");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-20" data-testid="articles-list-page">
      <p className="text-xs uppercase tracking-[0.2em] text-[#970C10] font-bold mb-3">Travel Guides</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Long-form guides for Pakistan travellers.</h1>
      <p className="mt-4 text-slate-500 max-w-2xl">Umrah timings, Gulf routes, baggage tips and airline comparisons. Written by our editorial team, reviewed by booking agents.</p>

      <div className="mt-12 grid sm:grid-cols-2 gap-6">
        {items.length === 0 && <p className="text-slate-400 text-sm">No articles yet. Check back soon.</p>}
        {items.map((a) => (
          <Link key={a.slug} to={`/articles/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group block border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-200">
            {a.hero_image_url && (
              <img src={a.hero_image_url} alt={a.title} className="w-full h-48 object-cover group-hover:scale-[1.02] transition-transform duration-500" />
            )}
            <div className="p-5">
              <p className="text-xs text-slate-400 font-semibold flex items-center gap-1.5"><Calendar className="w-3 h-3" />{fmt(a.published_at)}</p>
              <h2 className="mt-2 font-heading text-lg font-bold text-slate-900 leading-tight">{a.title}</h2>
              {a.excerpt && <p className="mt-2 text-sm text-slate-500 line-clamp-2">{a.excerpt}</p>}
              <p className="mt-4 inline-flex items-center gap-1 text-xs text-[#970C10] font-bold">Read guide <ArrowRight className="w-3 h-3" /></p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  usePageMeta(article?.seo_title || article?.title, article?.seo_description || article?.excerpt);

  const jsonLd = useMemo(() => {
    if (!article) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "https://sellvago.com";
    const url = `${origin}/articles/${slug}`;
    const graph = [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Travel Guides", "item": `${origin}/articles` },
          { "@type": "ListItem", "position": 2, "name": article.title, "item": url },
        ],
      },
      {
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt || article.seo_description,
        "image": article.hero_image_url || undefined,
        "author": { "@type": "Organization", "name": article.author || "Sellvago Team" },
        "publisher": { "@type": "Organization", "name": "Sellvago Farevault", "logo": { "@type": "ImageObject", "url": `${origin}/logos/sellvago-submark-red.png` } },
        "datePublished": article.published_at || undefined,
        "dateModified": article.updated_at || undefined,
        "mainEntityOfPage": url,
        "keywords": (article.tags || []).join(", "),
      },
    ];
    if (article.faqs?.length) {
      graph.push({
        "@type": "FAQPage",
        "mainEntity": article.faqs.map((f) => ({
          "@type": "Question", "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      });
    }
    return { "@context": "https://schema.org", "@graph": graph };
  }, [article, slug]);

  useJsonLd("article-detail", jsonLd);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="article-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Article not found</h1>
        <Link to="/articles" className="mt-6 inline-flex items-center gap-2 text-[#970C10] font-bold text-sm">See all guides <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }
  if (!article) return <div className="min-h-[400px] flex items-center justify-center text-slate-400 text-sm">Loading…</div>;

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-14 pb-20" data-testid="article-detail-page">
      <p className="text-xs text-slate-400 flex items-center gap-3 font-semibold">
        <Link to="/articles" className="hover:text-[#970C10] transition-colors duration-200">← All guides</Link>
        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmt(article.published_at)}</span>
      </p>
      <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 leading-tight" data-speakable>{article.title}</h1>
      {article.excerpt && <p className="mt-4 text-lg text-slate-500 leading-relaxed" data-speakable>{article.excerpt}</p>}
      {article.hero_image_url && <img src={article.hero_image_url} alt={article.title} className="mt-8 w-full rounded-2xl" />}

      <div className="mt-10 prose prose-slate max-w-none [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-3 [&_p]:leading-relaxed [&_p]:text-slate-700 [&_a]:text-[#970C10] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1" data-testid="article-body">
        <ReactMarkdown>{article.body_markdown || ""}</ReactMarkdown>
      </div>

      {article.tags?.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-2">
          {article.tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-semibold px-3 py-1 rounded-full"><Tag className="w-3 h-3" />{t}</span>
          ))}
        </div>
      )}

      {article.faqs?.length > 0 && (
        <section className="mt-14">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Frequently asked questions</h2>
          <div className="space-y-4" data-speakable>
            {article.faqs.map((f, i) => (
              <div key={i} className="border-l-2 border-[#970C10] pl-4">
                <p className="font-semibold text-slate-900">{f.q}</p>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
